import React, { useEffect, useMemo, useRef, useState } from 'react'
import FetchAccountHealthReportService from '../../../../../../Service/DashboardServices/AccountHealthServices/FetchAccountHealthReportService';

const MARKETPLACE_IDS = {
  US: "ATVPDKIKX0DER",
  CA: "A2EUQ1WTGCTBG2",
  MX: "A1AM78C64UM0Y8",
  IN: "A21TJRUUN4KGV",
  UK: "A1F83G8C2ARO7P",
  GB: "A1F83G8C2ARO7P",
  DE: "A1PA6795UKMFR9",
};

const GROUPS = ["Customer service", "Shipping", "Invoices", "Policy compliance"];

const POLICY_METRICS = [
  ["listingPolicyViolations", "Listing policy violations"],
  ["suspectedIntellectualPropertyViolations", "Suspected IP violations"],
  ["receivedIntellectualPropertyComplaints", "IP complaints received"],
  ["productAuthenticityCustomerComplaints", "Authenticity complaints"],
  ["productConditionCustomerComplaints", "Product condition complaints"],
  ["productSafetyCustomerComplaints", "Product safety complaints"],
  ["foodAndProductSafetyIssues", "Food and product safety issues"],
  ["restrictedProductPolicyViolations", "Restricted product violations"],
  ["customerProductReviewsPolicyViolations", "Review policy violations"],
  ["documentRequests", "Document requests"],
  ["otherPolicyViolations", "Other policy violations"],
];

const BAD_STATUSES = new Set(["BAD", "POOR", "CRITICAL", "AT_RISK", "UNHEALTHY", "DEACTIVATED", "SUSPENDED"]);
const GOOD_STATUSES = new Set(["GOOD", "GREAT", "NONE", "HEALTHY", "NORMAL"]);
const LEVEL_RANK = { grey: 0, green: 1, amber: 2, red: 3 };
const LEVEL_TEXT = { red: "Needs action", amber: "Watch", green: "On target", grey: "No data" };
const LEVEL_COLOR = { red: "danger", amber: "warning", green: "success", grey: "secondary" };
const DASH = "\u2013";
const orDefault = (value, fallback) => (value == null ? fallback : value);
const countOf = (field) => (m) => (m[field] || {}).count;

const formatDate = (value, options = { month: "short", day: "numeric" }) =>
  value ? new Date(value).toLocaleDateString("en-US", { ...options, timeZone: "UTC" }) : "";
const weekday = (date) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
const formatRange = (range) =>
  range ? `${formatDate(range.reportingDateFrom)} to ${formatDate(range.reportingDateTo)}` : "";
const rangeDays = (range) =>
  range ? Math.round((new Date(range.reportingDateTo) - new Date(range.reportingDateFrom)) / 864e5) : 0;
const addDays = (date, days) => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const formatPercent = (rate) => {
  if (rate == null) return DASH;
  const pct = rate * 100;
  return (Number.isInteger(pct) ? pct.toFixed(0) : pct < 10 ? pct.toFixed(2) : pct.toFixed(1)) + "%";
};
const formatPercentShort = (rate) => {
  if (rate == null) return DASH;
  const pct = rate * 100;
  return (Number.isInteger(pct) ? pct : pct < 10 ? pct.toFixed(1) : pct.toFixed(0)) + "%";
};
const formatTarget = (cond, target, isRate) => {
  const value = isRate ? formatPercent(target) : target;
  if (cond === "LESS_THAN") return `Under ${value}`;
  if (cond === "GREATER_THAN") return `Above ${value}`;
  if (cond === "EQUALS") return `Must be ${value}`;
  return "";
};
const humanize = (value) =>
  value ? String(value).toLowerCase().replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : DASH;

const statusLevel = (status) => {
  if (!status) return null;
  const s = String(status).toUpperCase();
  return BAD_STATUSES.has(s) ? "red" : GOOD_STATUSES.has(s) ? "green" : "amber";
};
const worstLevel = (...levels) =>
  levels.filter(Boolean).reduce((worst, l) => (LEVEL_RANK[l] > LEVEL_RANK[worst] ? l : worst), "green");
const ratingLevel = (score, status) =>
  worstLevel(statusLevel(status), score == null ? null : score < 100 ? "red" : score < 200 ? "amber" : "green");

const byKey = (rows) => Object.fromEntries(rows.map((r) => [r.key, r]));
const forMarketplace = (list, marketplaceId) =>
  Array.isArray(list) ? list.find((m) => m.marketplaceId === marketplaceId) || list[0] : undefined;
const marketplaceMetrics = (report, marketplaceId) => forMarketplace((report || {}).performanceMetrics, marketplaceId);
const marketplaceAccount = (report, marketplaceId) => forMarketplace((report || {}).accountStatuses, marketplaceId);

function extractMetrics(metrics) {
  const rows = [];
  if (!metrics) return rows;
  const addRate = (key, label, group, m, numField, denField, numLabel) => {
    if (!m) return;
    rows.push({
      key, label, group, kind: "rate",
      value: m.rate, status: m.status,
      num: typeof numField === "function" ? numField(m) : m[numField],
      den: m[denField], numLabel,
      target: m.targetValue, cond: m.targetCondition, range: m.reportingDateRange,
    });
  };
  const odr = metrics.orderDefectRate || {};
  addRate("odr_afn", "Order defect rate, FBA", "Customer service", odr.afn, countOf("orderWithDefects"), "orderCount", "orders with defects");
  addRate("odr_mfn", "Order defect rate, seller fulfilled", "Customer service", odr.mfn, countOf("orderWithDefects"), "orderCount", "orders with defects");

  const lateList = (metrics.lateShipmentRateList || []).length
    ? metrics.lateShipmentRateList
    : metrics.lateShipmentRate ? [metrics.lateShipmentRate] : [];
  [...lateList]
    .sort((a, b) => rangeDays(a.reportingDateRange) - rangeDays(b.reportingDateRange))
    .forEach((m) => {
      const days = rangeDays(m.reportingDateRange);
      addRate(`lsr_${days}`, `Late shipment rate, ${days} days`, "Shipping", m, "lateShipmentCount", "orderCount", "late shipments");
    });

  addRate("cancel", "Pre-fulfillment cancellation rate", "Shipping", metrics.preFulfillmentCancellationRate, "cancellationCount", "orderCount", "cancellations");
  addRate("vtr", "Valid tracking rate", "Shipping", metrics.validTrackingRate, "validTrackingCount", "shipmentCount", "with valid tracking");
  addRate("otdr", "On-time delivery rate", "Shipping", metrics.onTimeDeliveryRate, "onTimeDeliveryCount", "shipmentCountWithValidTracking", "on time");
  addRate("uotdr", "Unit on-time delivery rate", "Shipping", metrics.unitOnTimeDeliveryRate, "unitOnTimeDeliveryCount", "totalUnitCount", "units on time");
  addRate("idr", "Invoice defect rate", "Invoices", metrics.invoiceDefectRate, countOf("invoiceDefect"), "orderCount", "invoice defects");

  POLICY_METRICS.forEach(([key, label]) => {
    const m = metrics[key];
    if (m) rows.push({
      key, label, group: "Policy compliance", kind: "count",
      value: m.defectsCount, status: m.status,
      target: m.targetValue, cond: m.targetCondition, range: m.reportingDateRange,
    });
  });
  const warnings = metrics.policyViolationWarnings;
  if (warnings) rows.push({
    key: "warnings", label: "Policy violation warnings", group: "Policy compliance", kind: "count",
    value: warnings.warningsCount, target: 0, cond: "EQUALS", range: warnings.reportingDateRange,
  });
  return rows;
}

function evaluateMetric(m, previous) {
  let level = statusLevel(m.status) || "green";
  const notes = [];
  if (m.kind === "rate") {
    if (!m.den) return { ...m, level: "grey", notes: ["No orders in this period"] };
    const { value, target, cond } = m;
    if (cond === "LESS_THAN" && target != null) {
      if (value >= target) { level = "red"; notes.push("Over Amazon's limit"); }
      else if (value >= target * 0.75) { level = worstLevel(level, "amber"); notes.push("Close to the limit"); }
    }
    if (cond === "GREATER_THAN" && target != null) {
      if (value <= target) { level = "red"; notes.push("Under Amazon's minimum"); }
      else if (value < target + (1 - target) * 0.25) { level = worstLevel(level, "amber"); notes.push("Close to the minimum"); }
    }
  } else if (m.value > 0) {
    level = worstLevel(level, m.key === "warnings" || statusLevel(m.status) === "red" ? "red" : "amber");
    notes.push(`${m.value} open ${m.value === 1 ? "issue" : "issues"}`);
  }
  let delta = null;
  if (previous && previous.value != null && m.value != null) {
    delta = m.value - previous.value;
    const worse = m.kind === "rate" && m.cond === "GREATER_THAN" ? delta < 0 : delta > 0;
    if (worse && Math.abs(delta) > 1e-9) {
      level = worstLevel(level, "amber");
      notes.push("Worse than the day before");
    }
  }
  return { ...m, level, notes, delta };
}

const evaluateReport = (report, previousReport, marketplaceId) => {
  const prev = byKey(extractMetrics(marketplaceMetrics(previousReport, marketplaceId)));
  return extractMetrics(marketplaceMetrics(report, marketplaceId)).map((m) => evaluateMetric(m, prev[m.key]));
};

function useAccountHealth(token, request, enabled) {
  const [result, setResult] = useState({ state: "idle", data: null });
  const requestKey = JSON.stringify(request);
  useEffect(() => {
    if (!enabled || !request.integerID) return;
    let cancelled = false;
    setResult((prev) => ({ state: "loading", data: prev.data }));
    FetchAccountHealthReportService(token, request)
      .then((data) => { if (!cancelled) setResult({ state: "ready", data }); })
      .catch(() => { if (!cancelled) setResult((prev) => ({ state: "error", data: prev.data })); });
    return () => { cancelled = true; };
  }, [token, requestKey, enabled]);
  return result;
}

const Pill = ({ level, children }) => (
  <span className={`badge badge-pill badge-${LEVEL_COLOR[level]}`}>{children || LEVEL_TEXT[level]}</span>
);
const Empty = ({ error, children }) => (
  <div className={`card shadow-sm border-0 p-4 ${error ? "text-danger" : "text-muted"}`}>{children}</div>
);

const SCALE_TICKS = [
  ["0", { left: 0 }],
  ["100", { left: "10%", transform: "translateX(-50%)" }],
  ["200", { left: "20%", transform: "translateX(-50%)" }],
  ["1000", { right: 0 }],
];

function RatingScale({ score }) {
  const pos = Math.max(0, Math.min(1000, orDefault(score, 0))) / 10;
  return (
    <div className="mt-3" aria-hidden="true">
      <div className="position-relative">
        <div className="progress account-health__scale-bar">
          <div className="progress-bar bg-danger" style={{ width: "10%" }} />
          <div className="progress-bar bg-warning" style={{ width: "10%" }} />
          <div className="progress-bar bg-success" style={{ width: "80%" }} />
        </div>
        <span className="position-absolute account-health__scale-marker" style={{ left: `${pos}%` }} />
      </div>
      <div className="position-relative small text-muted account-health__scale-ticks">
        {SCALE_TICKS.map(([label, style]) => (
          <span key={label} className="position-absolute" style={style}>{label}</span>
        ))}
      </div>
    </div>
  );
}

function MetricRow({ m }) {
  const isRate = m.kind === "rate";
  const detail = isRate && m.den != null
    ? `${orDefault(m.num, 0)} of ${m.den} ${m.numLabel}`
    : isRate ? "" : m.value === 1 ? "1 issue" : `${orDefault(m.value, 0)} issues`;
  return (
    <div className={`row no-gutters align-items-center px-3 py-2 account-health__row account-health__row--${m.level}${m.level === "grey" ? " text-muted" : ""}`}>
      <div className="col-12 col-md-4 d-flex align-items-start">
        <span className={`rounded-circle flex-shrink-0 mt-2 mr-2 bg-${LEVEL_COLOR[m.level]} account-health__dot`} />
        <div>
          <div>{m.label}</div>
          <div className="small text-muted">{formatRange(m.range)}</div>
        </div>
      </div>
      <div className={`col-6 col-md-2 h6 mb-0 font-weight-bold${m.level === "red" ? " text-danger" : ""}`}>
        {isRate ? formatPercent(m.value) : orDefault(m.value, DASH)}
      </div>
      <div className="col-6 col-md-2 small text-muted">{detail}</div>
      <div className="col-6 col-md-2 small text-muted">{formatTarget(m.cond, m.target, isRate)}</div>
      <div className="col-6 col-md-2 text-right text-md-left">
        <Pill level={m.level} />
        {m.notes && m.notes.length > 0 && (
          <div className={`small mt-1 ${m.level === "red" ? "text-danger" : "text-muted"}`}>{m.notes.join(". ")}</div>
        )}
      </div>
    </div>
  );
}

function LatestReport({ data, marketplaceId }) {
  const view = useMemo(() => {
    const report = data && data.report;
    if (!report) return null;
    const metrics = marketplaceMetrics(report, marketplaceId) || {};
    const prevMetrics = marketplaceMetrics(data.previous_report, marketplaceId) || {};
    const rows = evaluateReport(report, data.previous_report, marketplaceId);
    const ahr = metrics.accountHealthRating || {};
    const prevScore = (prevMetrics.accountHealthRating || {}).ahrScore;
    return {
      rows,
      ahr,
      account: marketplaceAccount(report, marketplaceId),
      scoreDelta: prevScore != null && ahr.ahrScore != null ? ahr.ahrScore - prevScore : null,
      attention: rows
        .filter((r) => r.level === "red" || r.level === "amber")
        .sort((a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level]),
    };
  }, [data, marketplaceId]);

  if (!view) {
    return (
      <Empty>
        No account health report yet for this store.
        {data && data.today_job_status && ` Today's request is ${humanize(data.today_job_status).toLowerCase()}.`}
        {data && data.today_error && <div className="small text-danger mt-2">{data.today_error}</div>}
      </Empty>
    );
  }

  const { rows, ahr, scoreDelta, attention } = view;
  const account = view.account || {};
  const accountLevel = statusLevel(account.status) || "amber";
  const scoreLevel = ratingLevel(ahr.ahrScore, ahr.ahrStatus);
  const redCount = attention.filter((r) => r.level === "red").length;
  const amberCount = attention.length - redCount;
  const isOlder = data.today_snapshot_date && data.snapshot_date && data.today_snapshot_date !== data.snapshot_date;

  return (
    <>
      {isOlder && (
        <div className="alert alert-warning small py-2 mb-3">
          Showing the {formatDate(data.snapshot_date)} report. Today's report is{" "}
          {data.today_job_status ? humanize(data.today_job_status).toLowerCase() : "not requested yet"} and will appear here once Amazon finishes it.
        </div>
      )}
      <section className="row mb-4">
        <div className="col-lg-5 mb-3 mb-lg-0">
          <div className={`card shadow-sm h-100 account-health__card account-health__card--${scoreLevel}`}>
            <div className="card-body">
              <div className="small text-muted">Account Health Rating</div>
              <div className="d-flex align-items-center mt-2">
                <span className="h1 font-weight-bold mb-0 mr-3">{orDefault(ahr.ahrScore, DASH)}</span>
                <Pill level={scoreLevel}>{humanize(ahr.ahrStatus)}</Pill>
              </div>
              <RatingScale score={ahr.ahrScore} />
              <div className="small text-muted mt-2">
                {scoreDelta == null
                  ? "No earlier report to compare yet"
                  : scoreDelta === 0
                    ? "Same as the previous report"
                    : <span className={`font-weight-bold ${scoreDelta < 0 ? "text-danger" : "text-success"}`}>
                        {scoreDelta > 0 ? "Up" : "Down"} {Math.abs(scoreDelta)} points from the previous report
                      </span>}
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg mb-3 mb-lg-0">
          <div className={`card shadow-sm h-100 account-health__card account-health__card--${accountLevel}`}>
            <div className="card-body">
              <div className="small text-muted">Account status</div>
              <div className="h3 mt-2 mb-0">{humanize(account.status)}</div>
              <div className="small text-muted mt-2">
                {accountLevel === "green"
                  ? "Selling normally in this marketplace"
                  : "Selling may be restricted. Open Account Health in Seller Central."}
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg">
          <div className={`card shadow-sm h-100 account-health__card account-health__card--${redCount ? "red" : amberCount ? "amber" : "green"}`}>
            <div className="card-body">
              <div className="small text-muted">Metrics</div>
              <div className="h3 mt-2 mb-0">{attention.length === 0 ? "All on target" : `${attention.length} need attention`}</div>
              <div className="small text-muted mt-2">
                {rows.length} checked{redCount ? `, ${redCount} need action` : ""}{amberCount ? `, ${amberCount} to watch` : ""}
              </div>
            </div>
          </div>
        </div>
      </section>

      {attention.length > 0 && (
        <section className="mb-4">
          <h2 className="h5 mb-2">Needs attention</h2>
          <div className="card shadow-sm border-0 overflow-hidden">
            {attention.map((m) => <MetricRow key={m.key} m={m} />)}
          </div>
        </section>
      )}

      {GROUPS.map((group) => {
        const groupRows = rows.filter((r) => r.group === group);
        if (!groupRows.length) return null;
        return (
          <section className="mb-4" key={group}>
            <h2 className="h5 mb-2">{group}</h2>
            <div className="card shadow-sm border-0 overflow-hidden">
              <div className="d-none d-md-flex row no-gutters px-3 py-2 small text-muted bg-light account-health__row">
                <div className="col-md-4">Metric</div>
                <div className="col-md-2">Value</div>
                <div className="col-md-2">Detail</div>
                <div className="col-md-2">Amazon target</div>
                <div className="col-md-2">Status</div>
              </div>
              {groupRows.map((m) => <MetricRow key={m.key} m={m} />)}
            </div>
          </section>
        );
      })}
    </>
  );
}

function TrendChart({ cols }) {
  const points = cols
    .map((c, i) => ({ i, s: c.missing ? null : c.ahr.ahrScore, date: c.date }))
    .filter((p) => p.s != null);
  if (points.length < 2) return null;
  const width = 640, height = 120, pad = 10;
  const min = Math.min(...points.map((p) => p.s));
  const max = Math.max(...points.map((p) => p.s));
  const lo = Math.max(0, min - 20);
  const hi = max + 20;
  const x = (i) => pad + (i / Math.max(1, cols.length - 1)) * (width - 2 * pad);
  const y = (s) => height - pad - ((s - lo) / (hi - lo)) * (height - 2 * pad);
  const path = points.map((p, idx) => `${idx ? "L" : "M"}${x(p.i).toFixed(1)},${y(p.s).toFixed(1)}`).join(" ");
  const last = points[points.length - 1];
  return (
    <div className="card shadow-sm border-0 px-3 pt-3 pb-2 mb-3">
      <div className="small text-muted mb-1">Health rating trend</div>
      <svg className="d-block w-100 text-primary" height="120" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Health rating from ${points[0].s} to ${last.s}`}>
        {lo < 200 && hi > 200 && (
          <line x1={pad} x2={width - pad} y1={y(200)} y2={y(200)} className="text-danger" stroke="currentColor" strokeDasharray="4 4" />
        )}
        <path d={path} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        {points.map((p) => <circle key={p.date} cx={x(p.i)} cy={y(p.s)} r="2.5" fill="currentColor" />)}
        <circle cx={x(last.i)} cy={y(last.s)} r="5" className="text-success" fill="currentColor" stroke="#343a40" strokeWidth="1.5" />
      </svg>
      <div className="d-flex justify-content-between small text-muted">
        <span>{formatDate(cols[0].date)}</span>
        <span>{last.s} latest</span>
      </div>
    </div>
  );
}

const METRIC_CLASS = "text-left px-3 py-2 account-health__grid-metric";
const CELL_CLASS = "text-center text-nowrap px-1 py-2 account-health__cell";
const LEGEND = [
  ["green", "On target"],
  ["amber", "Watch"],
  ["red", "Needs action"],
  ["grey", "No orders"],
  ["missing", "No report"],
];

function HistoryView({ reports, marketplaceId, range, onRange, onPick }) {
  const scrollRef = useRef(null);
  const view = useMemo(() => {
    if (!reports.length) return null;
    const byDate = Object.fromEntries(reports.map((r) => [r.snapshot_date, r]));
    const newest = reports[0].snapshot_date;
    const dates = Array.from({ length: range }, (_, i) => addDays(newest, i - range + 1));
    const before = reports.find((r) => r.snapshot_date < dates[0]);
    let prev = before ? byKey(extractMetrics(marketplaceMetrics(before.report, marketplaceId))) : {};
    const cols = dates.map((date) => {
      const snap = byDate[date];
      if (!snap) return { date, missing: true, ahr: {}, account: {} };
      const metrics = marketplaceMetrics(snap.report, marketplaceId) || {};
      const rows = extractMetrics(metrics).map((m) => evaluateMetric(m, prev[m.key]));
      prev = byKey(rows);
      const flagged = rows.filter((r) => r.level === "red" || r.level === "amber");
      return {
        date,
        byKey: byKey(rows),
        ahr: metrics.accountHealthRating || {},
        account: marketplaceAccount(snap.report, marketplaceId) || {},
        flagged: flagged.length,
        red: flagged.some((r) => r.level === "red"),
      };
    });
    const defs = [];
    const seen = new Set();
    [...cols].reverse().forEach((c) => {
      if (c.missing) return;
      Object.values(c.byKey).forEach((m) => {
        if (seen.has(m.key)) return;
        seen.add(m.key);
        defs.push({ key: m.key, label: m.label, group: m.group, kind: m.kind });
      });
    });
    const present = cols.filter((c) => !c.missing);
    return {
      cols,
      defs,
      daysWithIssues: present.filter((c) => c.flagged > 0).length,
      daysWithReport: present.length,
      scores: present.map((c) => c.ahr.ahrScore).filter((s) => s != null),
    };
  }, [reports, marketplaceId, range]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
  }, [view]);

  if (!view) return <Empty>No reports saved yet. History fills in as the daily report is fetched.</Empty>;

  const { cols, defs, daysWithIssues, daysWithReport, scores } = view;
  const cell = (col, content, level, title) =>
    col.missing
      ? <td key={col.date} className={`${CELL_CLASS} account-health__level--missing`} title={`${formatDate(col.date)}: no report`}>{"\u00B7"}</td>
      : <td key={col.date} className={`${CELL_CLASS} account-health__level--${level}`} title={title}>{content}</td>;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div className="mb-2 mb-md-0">
          <strong>{daysWithIssues}</strong> of {daysWithReport} days had something to watch or fix.
          {scores.length > 0 && <> Health rating ranged from <strong>{Math.min(...scores)}</strong> to <strong>{Math.max(...scores)}</strong>.</>}
        </div>
        <div className="btn-group btn-group-sm" role="group" aria-label="Date range">
          {[14, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              className={`btn btn-outline-secondary${range === d ? " active" : ""}`}
              aria-pressed={range === d}
              onClick={() => onRange(d)}
            >
              {d} days
            </button>
          ))}
        </div>
      </div>
      <TrendChart cols={cols} />
      <div className="d-flex flex-wrap align-items-center small text-muted mb-2">
        {LEGEND.map(([level, label]) => (
          <span key={level} className="d-inline-flex align-items-center mr-3">
            <i className={`d-inline-block rounded mr-1 account-health__swatch account-health__level--${level}`} />{label}
          </span>
        ))}
        <span className="ml-md-auto">Select a date to open that day's full report.</span>
      </div>
      <div className="bg-white rounded shadow-sm overflow-auto" ref={scrollRef}>
        <table className="small account-health__grid">
          <thead>
            <tr>
              <th className="text-left text-muted font-weight-normal px-3 py-2 bg-light border-bottom account-health__grid-metric" scope="col">Metric</th>
              {cols.map((c) => (
                <th key={c.date} scope="col" className="bg-light border-bottom account-health__grid-day">
                  {c.missing ? (
                    <span className="d-flex flex-column align-items-center p-1 text-muted">
                      <span>{formatDate(c.date)}</span><small>{weekday(c.date)}</small>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-link btn-sm d-flex flex-column align-items-center w-100 p-1 font-weight-bold text-dark"
                      onClick={() => onPick(c.date)}
                      aria-label={`Open report for ${formatDate(c.date)}`}
                    >
                      <span>{formatDate(c.date)}</span><small className="font-weight-normal text-muted">{weekday(c.date)}</small>
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="account-health__grid-key">
              <th className={`${METRIC_CLASS} font-weight-bold`} scope="row">Needs attention</th>
              {cols.map((c) => cell(c, c.flagged || "0", c.red ? "red" : c.flagged ? "amber" : "green", `${formatDate(c.date)}: ${c.flagged} metrics flagged`))}
            </tr>
            <tr className="account-health__grid-key">
              <th className={`${METRIC_CLASS} font-weight-bold`} scope="row">Health rating</th>
              {cols.map((c) => {
                const score = orDefault(c.ahr.ahrScore, DASH);
                return cell(c, score, ratingLevel(c.ahr.ahrScore, c.ahr.ahrStatus), `${formatDate(c.date)}: ${score} (${humanize(c.ahr.ahrStatus)})`);
              })}
            </tr>
            <tr className="account-health__grid-key">
              <th className={`${METRIC_CLASS} font-weight-bold`} scope="row">Account status</th>
              {cols.map((c) => {
                const level = statusLevel(c.account.status) || "amber";
                return cell(c, level === "green" ? "OK" : humanize(c.account.status), level, `${formatDate(c.date)}: ${humanize(c.account.status)}`);
              })}
            </tr>
            {GROUPS.map((group) => {
              const groupDefs = defs.filter((d) => d.group === group);
              if (!groupDefs.length) return null;
              return (
                <React.Fragment key={group}>
                  <tr>
                    <th className={`${METRIC_CLASS} pt-3 text-primary font-weight-bold`} scope="rowgroup">{group}</th>
                    <td colSpan={cols.length} />
                  </tr>
                  {groupDefs.map((def) => (
                    <tr key={def.key}>
                      <th className={`${METRIC_CLASS} font-weight-normal`} scope="row">{def.label}</th>
                      {cols.map((c) => {
                        if (c.missing) return cell(c);
                        const m = c.byKey[def.key];
                        if (!m) return <td key={c.date} className={`${CELL_CLASS} account-health__level--grey`}>{DASH}</td>;
                        const text = m.level === "grey" ? DASH : def.kind === "rate" ? formatPercentShort(m.value) : orDefault(m.value, DASH);
                        const counts = m.kind === "rate" && m.den != null ? ` (${orDefault(m.num, 0)} of ${m.den})` : "";
                        const notes = m.notes && m.notes.length ? `. ${m.notes.join(". ")}` : "";
                        const title = `${def.label}, ${formatDate(c.date)}: ${m.level === "grey" ? "no orders" : text}${counts}${notes}`;
                        return cell(c, text, m.level, title);
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function StoreOverviewAccountHealth(props) {
  const marketplaceId = props.marketplaceId || MARKETPLACE_IDS[String(props.location || "").toUpperCase()] || MARKETPLACE_IDS.US;
  const [tab, setTab] = useState("latest");
  const [range, setRange] = useState(30);
  const [pickedDate, setPickedDate] = useState(null);

  const latest = useAccountHealth(props.token, { integerID: props.profileId, marketplaceId }, true);
  const history = useAccountHealth(props.token, { integerID: props.profileId, marketplaceId, days: range }, tab !== "latest");

  const reports = useMemo(
    () => [...((history.data || {}).reports || [])].sort((a, b) => (a.snapshot_date < b.snapshot_date ? 1 : -1)),
    [history.data]
  );

  let content;
  let snapshot = null;
  let fetchedAt = null;
  if (tab === "history") {
    content = history.state === "error"
      ? <Empty error>Couldn't load history. Please try again later.</Empty>
      : history.state !== "ready"
        ? <Empty>Loading history{"\u2026"}</Empty>
        : <HistoryView reports={reports} marketplaceId={marketplaceId} range={range} onRange={setRange}
            onPick={(date) => { setPickedDate(date); setTab("day"); }} />;
  } else if (tab === "day") {
    const idx = reports.findIndex((r) => r.snapshot_date === pickedDate);
    const day = reports[idx];
    const dayBefore = reports[idx + 1];
    const dayData = day && { ...day, previous_report: dayBefore ? dayBefore.report : null, today_snapshot_date: day.snapshot_date };
    snapshot = day ? day.snapshot_date : null;
    fetchedAt = day ? day.fetched_at : null;
    content = (
      <>
        <button type="button" className="btn btn-link p-0 mb-3" onClick={() => setTab("history")}>{"\u2039"} Back to history</button>
        {dayData ? <LatestReport data={dayData} marketplaceId={marketplaceId} /> : <Empty>No report for this day.</Empty>}
      </>
    );
  } else {
    snapshot = (latest.data || {}).snapshot_date;
    fetchedAt = (latest.data || {}).fetched_at;
    content = latest.state === "error"
      ? <Empty error>Couldn't load account health. Check that you have access to this store, then reload the page.</Empty>
      : latest.state !== "ready"
        ? <Empty>Loading the latest report{"\u2026"}</Empty>
        : <LatestReport data={latest.data} marketplaceId={marketplaceId} />;
  }

  const activeTab = tab === "day" ? "history" : tab;
  const switchTab = (t) => { setTab(t); setPickedDate(null); };

  return (
    <div className="account-health mt-4 pt-2 pb-5 px-3 px-lg-4">
      <header className="d-flex justify-content-between align-items-end flex-wrap mt-2 mb-4">
        <div className="mr-3">
          <h1 className="h3 font-weight-bold mb-0">Account health</h1>
          {props.storeName && <div className="small text-primary mt-1">{props.storeName}</div>}
        </div>
        <div className="d-flex flex-column align-items-start align-items-md-end">
          <div className="btn-group mb-2" role="tablist" aria-label="Account health views">
            {[["latest", "Latest report"], ["history", "History"]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                className={`btn btn-sm ${activeTab === key ? "btn-primary" : "btn-light"}`}
                aria-selected={activeTab === key}
                onClick={() => switchTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {snapshot && activeTab !== "history" && (
            <div className="small text-muted">
              Report for {formatDate(snapshot, { month: "short", day: "numeric", year: "numeric" })}
              {fetchedAt && <>, fetched {new Date(fetchedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</>}
            </div>
          )}
        </div>
      </header>
      {content}
    </div>
  );
}

export default StoreOverviewAccountHealth
